import React, { useState, useEffect, useCallback } from "react";
import { Refresh } from "@mui/icons-material";
import { adminApiService } from "../../../utils/api";
import { Dashboard } from "./Dashboard";
import { ComputerList, type StatusFilter } from "./ComputerList";
import { SearchAndFilters } from "./SearchAndFilters";
import { ComputerDetailModal } from "./ComputerDetailModal";
import { Container, HeaderSection, Title, RefreshButton } from "./styled";
import {
  ComputerWithHealthResponseDto,
  HealthStatisticsResponseDto,
} from "@swagger/typescript-fetch-goflyer";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { toastError } from "../../../utils/pageUtil/toast";

interface FigmaHealthMonitorProps {
  autoRefreshInterval?: number;
}

// Main Compound Component
export const FigmaHealthMonitorPage: React.FC<FigmaHealthMonitorProps> = ({
  autoRefreshInterval = 30000, // 30 seconds default
}) => {
  const { t } = useTranslation();
  const [statistics, setStatistics] =
    useState<HealthStatisticsResponseDto | null>(null);
  const [computers, setComputers] = useState<ComputerWithHealthResponseDto[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [selectedComputer, setSelectedComputer] =
    useState<ComputerWithHealthResponseDto | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [computerToDelete, setComputerToDelete] =
    useState<ComputerWithHealthResponseDto | null>(null);

  const fetchStatistics = useCallback(async () => {
    try {
      const response =
        await adminApiService.figmaHealthMonitorAdminControllerGetHealthStatistics();
      setStatistics(response);
    } catch (error) {
      console.error("Error fetching health statistics:", error);
    }
  }, []);

  const fetchComputers = useCallback(async () => {
    try {
      const response =
        await adminApiService.figmaHealthMonitorAdminControllerGetComputersWithHealth();
      setComputers(response);
    } catch (error) {
      console.error("Error fetching computers:", error);
    }
  }, []);

  const fetchAllData = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        await Promise.all([fetchStatistics(), fetchComputers()]);
        setLastUpdated(new Date());
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [fetchStatistics, fetchComputers]
  );

  const handleRefresh = useCallback(() => {
    fetchAllData(true);
  }, [fetchAllData]);

  const handleComputerClick = useCallback(
    (computer: ComputerWithHealthResponseDto) => {
      setSelectedComputer(computer);
      setModalOpen(true);
    },
    []
  );

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedComputer(null);
  }, []);

  const handleDeleteClick = useCallback(
    (computer: ComputerWithHealthResponseDto) => {
      setComputerToDelete(computer);
      setDeleteModalOpen(true);
    },
    []
  );

  const handleOnDelete = useCallback(async () => {
    if (!computerToDelete?.id) return;

    try {
      await adminApiService.deleteOneBaseFigmaHealthMonitorAdminControllerFigmaHealthMonitor(
        computerToDelete.id
      );
      await fetchAllData(true);
      setDeleteModalOpen(false);
      setComputerToDelete(null);
    } catch (error) {
      console.error("Error deleting computer:", error);
      toastError(t(messages.Error_occurred_while_deleting()));
    }
  }, [computerToDelete, fetchAllData]);

  const handleCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setComputerToDelete(null);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  // Auto-refresh setup
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAllData(true);
    }, autoRefreshInterval);

    return () => clearInterval(interval);
  }, [autoRefreshInterval]);

  return (
    <Container>
      <HeaderSection>
        <div>
          <Title>{t(messages.Figma_Health_Monitor())}</Title>
          <p style={{ color: "#666", margin: "5px 0 0 0", fontSize: "14px" }}>
            {t(messages.Last_updated())} {lastUpdated.toLocaleTimeString()}
          </p>
        </div>
        <RefreshButton onClick={handleRefresh} disabled={refreshing}>
          <Refresh
            style={{
              animation: refreshing ? "spin 1s linear infinite" : "none",
            }}
          />
          {refreshing ? t(messages.Refreshing()) : t(messages.Refresh())}
        </RefreshButton>
      </HeaderSection>

      <Dashboard statistics={statistics} loading={loading} />

      <SearchAndFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        computerCount={computers.length}
      />

      <ComputerList
        computers={computers}
        loading={loading}
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        onComputerClick={handleComputerClick}
        onDeleteClick={handleDeleteClick}
      />

      <ComputerDetailModal
        computer={selectedComputer}
        open={modalOpen}
        onClose={handleModalClose}
      />

      <MuiConfirmationModal
        open={deleteModalOpen}
        handleCancel={handleCancel}
        handleConfirm={handleOnDelete}
        title={`${t(messages.Delete_Confirmation())}`}
        description={`${t(messages.Are_you_sure_you_want_to_delete_These())} ${
          computerToDelete?.computerName
        }? ${t(messages.This_action_cannot_be_undone())}.`}
      />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  );
};
